import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {translate} from "../../../translate/translate";
import {
    Box, Checkbox, Chip,
    CircularProgress, Fab,
    FormControl,
    Grid,
    InputLabel, ListItemText,
    MenuItem,
    Paper, Tooltip,
    Typography
} from "@mui/material";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import {Search, Upload} from "@mui/icons-material";
import {URLS} from "../../../services/app-urls";
import {PaginatedResult} from "../../../models/default/paginated-result";
import {ErrorComponent} from "../../../components/error/error.component";
import {Client} from "../../../models/basic/client";
import Button from "@mui/material/Button";
import {sortListObj} from "../../../utils/list-utils";
import {Form, Formik} from "formik";
import Select from "@mui/material/Select";
import {years} from "../../../models/default/year-month";
import {ImportRelatedCost, StatusImportRelatedCostLabel} from "../../../models/basic/import-related-cost";
import AddImportRelatedCostComponent from "./add-import-related-cost.component";
import {useFilters} from "../../../filters/base-filter";
import {usePagination} from "../../../utils/pagination";
import {ImportRelatedCostService, ImportRelatedCostsFilter} from "../../../services/import-related-cost-service";
import {AbstractService} from "../../../services/abstract-service";
import {trackPromise} from "react-promise-tracker";


const ImportRelatedCostComponent = () => {
    const service = new ImportRelatedCostService();
    const serviceClient = new AbstractService<Client>(URLS.CLIENT);
    const {filters, updateFilterKeepingParam, updateFilter } = useFilters<ImportRelatedCostsFilter>(
        { client: "", years: ""},
        ["client", "years"]
    );
    const [relatedCostList, setPoMonitoringList] = React.useState<PaginatedResult<ImportRelatedCost>>();

    const {
        dataSource,
        pagination,
        resetPage,
        handleChangePage,
        handleChangeRowsPerPage
    } = usePagination<ImportRelatedCost>(relatedCostList);
    const expand = ["client", "user"];

    const [dataSourceClient, setDataSourceClient] = React.useState<Client[]>([]);

    const [loading, setLoading] = React.useState(false);

    const [dataSourceYears, setDataSourceYears] = React.useState<{ years: number[] }>();
    const [selectedYears, setSelectedYears] = useState<any[]>([]);
    const [openImportDialog, setOpenImportDialog] = React.useState(false);

    const pollingRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        loadClients();
        loadYears();
        searchWithoutPolling();
        initializeYears();
        return () => stopPollingFn();
    }, []);

    const initializeYears = () => {
        const years = filters.years?.split(",");
        setSelectedYears(years?.map(year => parseInt(year)) || []);
    }

    const loadClients = () => {
        serviceClient.readAll().then(response => {
            const sortedData = sortListObj(response.data, "name");
            setDataSourceClient(sortedData);
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const loadYears = () => {
        service.listYears().then(response => {
            setDataSourceYears(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const searchWithoutPolling = () => {
        setLoading(true);
        service.readPaginated(filters, expand, pagination).then(response => {
            setPoMonitoringList(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoading(false);
        })
    }

    const search = (restartIndex = false) => {
        setLoading(true);
        stopPollingFn();
        pollingRef.current = setInterval(() => {
            trackPromise(
                service.readPaginated(filters, expand, pagination).then(response => {
                    setPoMonitoringList(response.data);
                    const noneProcessing = response.data.results.every(item => item.status !== 1);
                    if (noneProcessing) {
                        stopPollingFn();
                    }
                }).catch((error: any) => {
                    ErrorComponent(error);
                }).finally(() => {
                    setLoading(false);
                })
            )
        }, 10000);

        restartIndex && resetPage();
    };

    const stopPollingFn = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    useEffect(() => {
        search();
    }, [pagination]);

    const customCellRender = (column: keyof ImportRelatedCost, index: number, row: ImportRelatedCost) => {
        if (column === "status") {
           return <Tooltip title={row.error_message} placement="top">
               <Chip size="small"
                  color={StatusImportRelatedCostLabel[row.status].color as any}
                  label={translate.t(StatusImportRelatedCostLabel[row.status].label)}
               />
           </Tooltip>
        }
        return row[column];
    }

    const renderYears = (selected: any) => {
        const selects: any[] = [];
        selected.map((id: any) => {
            const description: any = years.find(year => id === year.id)?.description;
            selects.push(description);
        });

        return selects.join(", ");
    };

    return (
        <section>
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <Typography variant={"h5"}>
                    {translate.t("import_related_costs")}
                </Typography>
            </div>
            <div className="floating-button" style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                    color="secondary"
                    variant="contained"
                    startIcon={<Upload />}
                    onClick={() => {
                        setOpenImportDialog(true);
                    }}>{translate.t("import_related_costs_btn").toUpperCase()}
                </Button>
            </div>

            <Grid size={12}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Formik
                            enableReinitialize={true}
                            validateOnMount={true}
                            initialValues={{name: ""}}
                            onSubmit={(values, {setSubmitting}) => {
                                setTimeout(() => {
                                    searchWithoutPolling();
                                    search(true);
                                    setSubmitting(false);
                                }, 500);
                            }}
                        >
                            {
                                () => (
                                    <Form autoComplete="off">
                                        <Box alignItems="center" display="flex" justifyContent="space-between">
                                            <Grid alignItems="center" container spacing={2} direction={"row"} width="50%">
                                                <Grid >
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("client")}
                                                        </InputLabel>
                                                        <Select
                                                            name="client"
                                                            label={translate.t("client")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={filters.client || "0"}
                                                            onChange={(event) => updateFilterKeepingParam("client", event.target.value as string )}
                                                        >
                                                            <MenuItem key="0" value="0">
                                                                {translate.t('all')}
                                                            </MenuItem>
                                                            {dataSourceClient?.map((client: Client) => (
                                                                <MenuItem key={client.id} value={client.id}>
                                                                    {client.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={3}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("year")}
                                                        </InputLabel>
                                                        <Select
                                                            name="year"
                                                            label={translate.t("year")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={selectedYears}
                                                            onChange={(event: any) => {
                                                                const validValues = event.target.value
                                                                    .map((value: any) => Number(value))
                                                                    .filter((value: number) => Number.isFinite(value));
                                                                setSelectedYears(validValues);
                                                                updateFilterKeepingParam("years", validValues.join(","));
                                                            }}
                                                            multiple
                                                            renderValue={(selected) => renderYears(selected)}
                                                        >
                                                            {dataSourceYears?.years.map((year: any) => (
                                                                <MenuItem key={year} value={year}>
                                                                    <Checkbox checked={selectedYears.includes(year)}/>
                                                                    <ListItemText primary={year}/>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid >
                                                    <Fab
                                                        size="small"
                                                        type="submit"
                                                        color="secondary">
                                                        <Search/>
                                                    </Fab>
                                                </Grid>
                                            </Grid>
                                            {loading && <CircularProgress size="30px" />}
                                        </Box>
                                    </Form>
                                )}
                        </Formik>
                    </Box>
                </Paper>
            </Grid>

            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <TablePaginationComponent
                    state={dataSource}
                    displayedColumnsShow={["client", "import_date", "user", "import_status"]}
                    displayedColumns={["client.name", "created_at", "user.name", "status"]}
                    customColumns={["status"]}
                    dateColumns={["created_at"]}
                    customCellRender={customCellRender}
                    count={relatedCostList?.count}
                    next={relatedCostList?.next}
                    previous={relatedCostList?.previous}
                    page={pagination.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={pagination.limit}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 50, 100]}
                    loading={loading}
                />
            </Paper>

            <AddImportRelatedCostComponent
                openDialog={openImportDialog}
                handleCloseDialog={() => {
                    searchWithoutPolling();
                    search(true);
                    setOpenImportDialog(false);
                }}
                dataSourceClient={dataSourceClient}
            />
        </section>
    );
};

export default ImportRelatedCostComponent;
